import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { TimeoutEnum } from 'app/core/enum/timeout.enum';
import { Categories } from 'app/shared/interfaces/upload-file-support-page';
import { SupportPageService } from 'app/core/services/support-page.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  standalone: false,
  selector: 'app-category-management',
  templateUrl: './category-management.component.html',
  styleUrls: ['./category-management.component.scss'],
})
export class CategoryManagementComponent implements OnInit {
  uploadFormFile!: FormGroup;

  languages = [
    { code: 'ptBR', label: 'Português', flag: 'PT.png' },
    { code: 'en', label: 'Inglês', flag: 'EN.png' },
    { code: 'es', label: 'Espanhol', flag: 'ES.png' },
    { code: 'it', label: 'Italiano', flag: 'IT.png' },
  ];

  constructor(
    private fb: FormBuilder,
    private readonly supportPageService: SupportPageService,
    private readonly translateService: TranslateService,
    private readonly dialog: MatDialog,
    private readonly toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.uploadFormFile = this.fb.group({
      rows: this.fb.array([]),
    });
    this.supportPageService
      .getSupportFileById('categories')
      .subscribe((data: Categories) => {
        if (data) {
          this.patchFormFromJson(data);
        }
      });
  }

  patchFormFromJson(json: Categories): void {
    const categories = json.categories;
    const rowsArray = this.uploadFormFile.get('rows') as FormArray;

    rowsArray.clear();

    Object.entries(categories).forEach(([key, values]: [string, any]) => {
      const row = this.fb.group({
        key: [key],
        ptBR: [values.ptBR || '', Validators.required],
        en: [values.en || '', Validators.required],
        es: [values.es || '', Validators.required],
        it: [values.it || '', Validators.required],
      });

      row.get('en')?.valueChanges.subscribe((value: string) => {
        const currentKey = row.get('key')?.value;
        if (value && (!currentKey || currentKey.trim() === '')) {
          const generatedKey = this.toSnakeCase(value);
          row.get('key')?.setValue(generatedKey, { emitEvent: false });
        }
      });

      rowsArray.push(row);
    });
  }

  get rows(): FormArray {
    return this.uploadFormFile.get('rows') as FormArray;
  }

  createRow(): FormGroup {
    const row = this.fb.group({
      key: ['', Validators.required],
      ptBR: ['', Validators.required],
      en: ['', Validators.required],
      es: ['', Validators.required],
      it: ['', Validators.required],
    });

    row.get('en')?.valueChanges.subscribe((value) => {
      if (value) {
        const key = this.toSnakeCase(value);
        row.get('key')?.setValue(key, { emitEvent: false });
      }
    });

    return row;
  }

  addRow(): void {
    const row = this.createRow();
    this.rows.push(row);

    row.get('en')?.valueChanges.subscribe((value: string) => {
      if (value) {
        const key = this.toSnakeCase(value);
        row.get('key')?.setValue(key, { emitEvent: false });
      }
    });
  }

  toSnakeCase(input: string): string {
    return input.trim().toLowerCase().replace(/\s+/g, '_');
  }

  removeRow(index: number): void {
    this.rows.removeAt(index);
  }

  closeDialog(): void {
    this.dialog.closeAll();
  }

  onSubmit(): void {
    const rows = this.uploadFormFile.value.rows;
    const categories: Record<string, any> = {};

    rows.forEach((row: any) => {
      const key = row.key;
      categories[key] = {
        ptBR: row.ptBR,
        en: row.en,
        es: row.es,
        it: row.it,
      };
    });

    const newDocument: Categories = {
      id: 'categories',
      categories: categories,
    };

    this.supportPageService.postSupportFile(newDocument).subscribe(
      () => {
        this.translateService
          .get('global.alert.send-file-upload-success')
          .subscribe((msg) =>
            this.toastr.success(msg, '', {
              positionClass: 'toast-top-full-width',
              disableTimeOut: false,
            }),
          );
        this.uploadFormFile.reset();
        setTimeout(() => {
          this.dialog.closeAll();
        }, TimeoutEnum.Medium);
      },
      () => {
        this.translateService
          .get('global.support-error-save-file-in-db')
          .subscribe((msg) =>
            this.toastr.error(msg, '', {
              positionClass: 'toast-top-full-width',
              disableTimeOut: false,
            }),
          );
      },
    );
  }
}
